import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { Clock, TrendingUp, Users, Briefcase, AlertCircle, CheckSquare, Calendar, Monitor, AlertTriangle, CheckCircle2, Youtube, Bell, Map as MapIcon } from 'lucide-react';
import axios from 'axios';
import Avatar from './Avatar';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const API = process.env.REACT_APP_BACKEND_URL;

// Flag SVG component
const FlagIcon = ({ countryCode }) => {
  return (
    <img 
      src={`https://flagcdn.com/w20/${countryCode.toLowerCase()}.png`}
      srcSet={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png 2x`}
      width="20"
      alt={countryCode}
      className="rounded"
    />
  );
};

const currencyCountries = {
  'USD': 'US',
  'GBP': 'GB',
  'JPY': 'JP',
  'AED': 'AE',
  'CNY': 'CN',
  'INR': 'IN',
  'CHF': 'CH',
  'CAD': 'CA',
  'AUD': 'AU'
};

const CentralDashboard = () => {
  const [config, setConfig] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [news, setNews] = useState([]);
  const [worldTimes, setWorldTimes] = useState({});
  const [loading, setLoading] = useState(true);
  const [userAvatars, setUserAvatars] = useState({});
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [leftPanelView, setLeftPanelView] = useState('youtube'); // 'youtube' or 'updates'
  const [rightPanelView, setRightPanelView] = useState('map'); // 'map' or 'youtube'
  const [newUpdateIds, setNewUpdateIds] = useState(new Set());
  const previousUpdateIds = React.useRef(new Set());

  // Memoize marker icons to prevent re-creation on every render
  const markerIcons = useMemo(() => {
    const icons = {};
    const mapSettings = config?.map_settings || {};
    
    Object.keys(mapSettings).forEach(statusId => {
      const pinColor = mapSettings[statusId]?.color || '#3B82F6';
      icons[statusId] = new L.Icon({
        iconUrl: `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='${encodeURIComponent(pinColor)}' stroke='white' stroke-width='1'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/%3E%3C/svg%3E`,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });
    });
    
    // Default icon for unconfigured statuses
    icons.default = new L.Icon({
      iconUrl: `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%233B82F6' stroke='white' stroke-width='1'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/%3E%3C/svg%3E`,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    });
    
    return icons;
  }, [config?.map_settings]);

  // Fetch configuration
  useEffect(() => {
    fetchConfig();
  }, []);

  // Fetch live data at intervals
  useEffect(() => {
    if (config) {
      fetchLiveData();
      fetchExchangeRates();
      fetchNews();
      fetchRecentUpdates();
      
      const interval = setInterval(() => {
        fetchLiveData();
        fetchExchangeRates();
        fetchNews();
        fetchRecentUpdates();
      }, (config.refresh_interval || 30) * 1000);

      return () => clearInterval(interval);
    }
  }, [config]);

  // Update world clocks every second
  useEffect(() => {
    if (config?.world_clocks) {
      updateWorldTimes();
      const interval = setInterval(updateWorldTimes, 1000);
      return () => clearInterval(interval);
    }
  }, [config]);

  // Listen for custom event when a project update is created
  useEffect(() => {
    const handleUpdateCreated = (event) => {
      console.log('🔔 Project update created event received in Central Dashboard:', event.detail);
      // Immediately refresh the Recent Updates list
      fetchRecentUpdates();
    };
    
    window.addEventListener('projectUpdateCreated', handleUpdateCreated);
    
    return () => {
      window.removeEventListener('projectUpdateCreated', handleUpdateCreated);
    };
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No auth token found for dashboard');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API}/api/central-dashboard/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard config:', error);
      // Use defaults on error
      setConfig({
        world_clocks: [],
        exchange_rates: { base: 'EUR', currencies: ['USD', 'GBP', 'JPY', 'AED'] },
        employee_stats: ['projects_assigned', 'tasks_completed', 'open_incidents'],
        rss_feed_url: 'https://www.aviation24.be/feed/',
        refresh_interval: 30
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/central-dashboard/live-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Live data response:', response.data);
      console.log('Projects count:', response.data.projects?.length);
      console.log('Employee stats count:', response.data.employee_stats?.length);
      console.log('Advanced stats:', response.data.advanced_stats);
      setLiveData(response.data);
      
      // Fetch avatars for employees
      if (response.data.employee_stats?.length > 0) {
        const userIds = response.data.employee_stats.map(emp => emp.id);
        fetchAvatars(userIds);
      }
    } catch (error) {
      console.error('Failed to fetch live data:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const fetchAvatars = async (userIds) => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 Central Dashboard - Fetching avatars for:', userIds);
      const response = await axios.post(
        `${API}/api/users/avatars`,
        { user_ids: userIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const avatars = response.data.avatars || {};
      console.log('✅ Central Dashboard - Received avatars:', avatars);
      console.log('📊 Central Dashboard - Avatar count:', Object.keys(avatars).length, 'out of', userIds.length, 'requested');
      
      // Log which users have avatars and which don't
      userIds.forEach(userId => {
        if (avatars[userId]) {
          console.log(`  ✓ ${userId}: ${avatars[userId]}`);
        } else {
          console.log(`  ✗ ${userId}: No avatar URL`);
        }
      });
      
      // Merge new avatars with existing ones (never clear existing avatars)
      setUserAvatars(prev => {
        const merged = { ...prev, ...avatars };
        const changed = JSON.stringify(prev) !== JSON.stringify(merged);
        if (changed) {
          console.log('🔄 Avatar URLs changed, updating state');
        }
        return merged;
      });
    } catch (error) {
      console.error('❌ Failed to fetch avatars:', error);
    }
  };

  const fetchExchangeRates = async () => {
    try {
      const token = localStorage.getItem('token');
      const currencies = config?.exchange_rates?.currencies?.join(',') || 'USD,GBP,JPY,AED';
      const base = config?.exchange_rates?.base || 'EUR';
      
      const response = await axios.get(
        `${API}/api/central-dashboard/exchange-rates?base=${base}&currencies=${currencies}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExchangeRates(response.data);
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
    }
  };

  const fetchNews = async () => {
    try {
      const token = localStorage.getItem('token');
      const feedUrl = config?.rss_feed_url || 'https://www.aviation24.be/feed/';
      
      const response = await axios.get(
        `${API}/api/central-dashboard/news?feed_url=${encodeURIComponent(feedUrl)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNews(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch news:', error);
    }
  };

  const fetchRecentUpdates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/projects/recent-updates?limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updates = response.data?.updates || [];
      
      // Check for new updates using ref (persists between renders)
      if (previousUpdateIds.current.size > 0 && updates.length > 0) {
        const newIds = new Set();
        
        updates.forEach(update => {
          if (!previousUpdateIds.current.has(update.id)) {
            newIds.add(update.id);
            console.log('🆕 New update detected:', update.id, update.text?.substring(0, 50));
          }
        });
        
        if (newIds.size > 0) {
          console.log('🔔 Playing notification sound for', newIds.size, 'new updates');
          // Play notification sound
          const audio = new Audio('/notification.mp3');
          audio.volume = 0.5;
          audio.play().catch(e => console.log('Could not play notification sound:', e));
          
          // Mark new updates
          setNewUpdateIds(newIds);
          
          // Remove highlight after 5 seconds
          setTimeout(() => {
            setNewUpdateIds(new Set());
          }, 5000);
        }
      }
      
      // Update ref with current update IDs
      previousUpdateIds.current = new Set(updates.map(u => u.id));
      
      setRecentUpdates(updates);
      
      // Fetch avatars for update authors
      const authorIds = updates
        .map(u => u.author_id)
        .filter(id => id);
      
      if (authorIds.length > 0) {
        const uniqueIds = [...new Set(authorIds)];
        const avatarResponse = await axios.post(
          `${API}/api/users/avatars`,
          { user_ids: uniqueIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Merge with existing avatars instead of replacing
        setUserAvatars(prev => ({
          ...prev,
          ...(avatarResponse.data?.avatars || {})
        }));
      }
    } catch (error) {
      console.error('Failed to fetch recent updates:', error);
    }
  };

  const updateWorldTimes = () => {
    if (!config?.world_clocks) return;
    
    const times = {};
    config.world_clocks.forEach(clock => {
      try {
        const time = new Date().toLocaleTimeString('en-US', {
          timeZone: clock.timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        times[clock.name] = time;
      } catch (error) {
        times[clock.name] = 'Invalid';
      }
    });
    setWorldTimes(times);
  };

  const getCountryCoordinates = (countryCode) => {
    // Comprehensive country code to coordinates mapping
    const coords = {
      // Americas
      'US': [37.0902, -95.7129], 'CA': [56.1304, -106.3468], 'MX': [23.6345, -102.5528],
      'BR': [-14.2350, -51.9253], 'AR': [-38.4161, -63.6167], 'CL': [-35.6751, -71.5430],
      'CO': [4.5709, -74.2973], 'PE': [-9.1900, -75.0152], 'VE': [6.4238, -66.5897],
      'BB': [13.1939, -59.5432], 'GD': [12.1165, -61.679], 'AN': [12.2265, -68.9515],
      
      // Europe
      'GB': [55.3781, -3.4360], 'DE': [51.1657, 10.4515], 'FR': [46.2276, 2.2137],
      'ES': [40.4637, -3.7492], 'IT': [41.8719, 12.5674], 'NL': [52.1326, 5.2913],
      'BE': [50.5039, 4.4699], 'GR': [39.0742, 21.8243], 'DK': [56.2639, 9.5018],
      'SE': [60.1282, 18.6435], 'NO': [60.4720, 8.4689], 'FI': [61.9241, 25.7482],
      'PL': [51.9194, 19.1451], 'CZ': [49.8175, 15.4730], 'AT': [47.5162, 14.5501],
      'CH': [46.8182, 8.2275], 'PT': [39.3999, -8.2245], 'IE': [53.4129, -8.2439],
      
      // Africa
      'ZA': [-30.5595, 22.9375], 'EG': [26.8206, 30.8025], 'NG': [9.0820, 8.6753],
      'KE': [-0.0236, 37.9062], 'TZ': [-6.3690, 34.8888], 'UG': [1.3733, 32.2903],
      'GH': [7.9465, -1.0232], 'CI': [7.5400, -5.5471], 'SN': [14.4974, -14.4524],
      'MA': [31.7917, -7.0926], 'DZ': [28.0339, 1.6596], 'TN': [33.8869, 9.5375],
      'AO': [-11.2027, 17.8739], 'MZ': [-18.6657, 35.5296], 'ZW': [-19.0154, 29.1549],
      'BW': [-22.3285, 24.6849], 'NA': [-22.9576, 18.4904], 'ZM': [-13.1339, 27.8493],
      'MW': [-13.2543, 34.3015], 'RW': [-1.9403, 29.8739], 'BI': [-3.3731, 29.9189],
      'ET': [9.1450, 40.4897], 'SO': [5.1521, 46.1996], 'SD': [12.8628, 30.2176],
      'LY': [26.3351, 17.2283], 'CV': [16.5388, -23.0418],
      
      // Middle East
      'AE': [23.4241, 53.8478], 'SA': [23.8859, 45.0792], 'QA': [25.3548, 51.1839],
      'KW': [29.3117, 47.4818], 'BH': [26.0667, 50.5577], 'OM': [21.4735, 55.9754],
      'IL': [31.0461, 34.8516], 'JO': [30.5852, 36.2384], 'LB': [33.8547, 35.8623],
      'IQ': [33.2232, 43.6793], 'IR': [32.4279, 53.6880], 'TR': [38.9637, 35.2433],
      
      // Asia
      'CN': [35.8617, 104.1954], 'JP': [36.2048, 138.2529], 'IN': [20.5937, 78.9629],
      'TH': [15.8700, 100.9925], 'VN': [14.0583, 108.2772], 'PH': [12.8797, 121.7740],
      'ID': [-0.7893, 113.9213], 'MY': [4.2105, 101.9758], 'SG': [1.3521, 103.8198],
      'KR': [35.9078, 127.7669], 'BD': [23.685, 90.3563], 'PK': [30.3753, 69.3451],
      'MM': [21.9162, 95.9560], 'KH': [12.5657, 104.9910], 'LA': [19.8563, 102.4955],
      'NP': [28.3949, 84.1240], 'LK': [7.8731, 80.7718], 'MN': [46.8625, 103.8467],
      'KZ': [48.0196, 66.9237], 'UZ': [41.3775, 64.5853],
      
      // Oceania
      'AU': [-25.2744, 133.7751], 'NZ': [-40.9006, 174.8860], 'FJ': [-17.7134, 178.0650],
      'PG': [-6.3150, 143.9555], 'NC': [-20.9043, 165.6180]
    };
    return coords[countryCode] || [0, 0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900 text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-xl">Loading Central Dashboard...</p>
        </div>
      </div>
    );
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  // Extract YouTube video ID from URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      let videoId = '';
      
      if (urlObj.hostname.includes('youtube.com')) {
        videoId = urlObj.searchParams.get('v');
      } else if (urlObj.hostname.includes('youtu.be')) {
        videoId = urlObj.pathname.slice(1);
      }
      
      return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0` : '';
    } catch (e) {
      return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 text-white overflow-hidden flex flex-col">
      {/* Header - Logo, World Clocks, Local Time */}
      <div className="bg-gradient-to-r from-blue-900 to-purple-900 p-3 shadow-lg flex-shrink-0">
        <div className="flex items-center justify-between">
          {/* Logo with fullscreen toggle */}
          <button
            onClick={toggleFullscreen}
            className="flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            title="Click to toggle fullscreen"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_taskfix-dashboard/artifacts/av5zxqge_logo-trans-big.png" 
              alt="Velocity Logo" 
              className="h-12"
            />
          </button>

          {/* World Clocks */}
          {config?.world_clocks && config.world_clocks.length > 0 && (
            <div className="flex gap-6 mx-8">
              {config.world_clocks.map((clock, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-xs text-blue-200">{clock.name}</div>
                  <div className="text-lg font-mono font-bold">
                    {worldTimes[clock.name] || '--:--:--'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Local Time and Date */}
          <div className="text-right flex-shrink-0">
            <div className="text-3xl font-mono font-bold">
              {new Date().toLocaleTimeString('en-US', { hour12: false })}
            </div>
            <div className="text-sm text-blue-200">
              {new Date().toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 pb-0 overflow-hidden flex flex-col">
        {/* General Stats - Single row */}
        <div className="grid grid-cols-6 gap-3 mb-3 flex-shrink-0">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-200 text-xs">Total Projects</p>
                <p className="text-xl font-bold">{(liveData?.projects?.length || 0)}</p>
              </div>
              <Briefcase className="w-6 h-6 text-blue-200" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200 text-xs">Total Tasks</p>
                <p className="text-xl font-bold">{(liveData?.advanced_stats?.total_tasks || 0)}</p>
              </div>
              <CheckSquare className="w-6 h-6 text-purple-200" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-200 text-xs">Hours This Week</p>
                <p className="text-xl font-bold">{(liveData?.advanced_stats?.hours_this_week || 0)}h</p>
              </div>
              <Clock className="w-6 h-6 text-green-200" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-lg p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-200 text-xs">Total Incidents</p>
                <p className="text-xl font-bold">{(liveData?.advanced_stats?.total_incidents || 0)}</p>
              </div>
              <AlertCircle className="w-6 h-6 text-yellow-200" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-lg p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-200 text-xs">Unsolved Incidents</p>
                <p className="text-xl font-bold">{(liveData?.advanced_stats?.open_incidents || 0)}</p>
              </div>
              <AlertCircle className="w-6 h-6 text-red-200" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-lg p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-200 text-xs">Past Deadlines</p>
                <p className="text-xl font-bold">{(liveData?.advanced_stats?.projects_past_deadline || 0)}</p>
              </div>
              <Calendar className="w-6 h-6 text-orange-200" />
            </div>
          </div>
        </div>

        {/* Main Content - Map, Incidents, Currency */}
        <div className="flex-1 flex flex-col gap-3 overflow-hidden">
          <div className="flex gap-3" style={{height: '65%'}}>
            {/* Left Panel - YouTube or Updates */}
            <div className="flex-1 bg-gray-800 rounded-lg p-3 flex flex-col min-w-0" style={{flexBasis: '50%'}}>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  {leftPanelView === 'youtube' ? (
                    <>
                      <Youtube className="w-5 h-5" />
                      Live Stream
                    </>
                  ) : (
                    <>
                      <Bell className="w-5 h-5" />
                      Recent Updates
                    </>
                  )}
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setLeftPanelView('youtube')}
                    className={`p-2 rounded transition-colors ${
                      leftPanelView === 'youtube' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    title="YouTube Stream"
                  >
                    <Youtube className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setLeftPanelView('updates')}
                    className={`p-2 rounded transition-colors ${
                      leftPanelView === 'updates' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    title="Recent Updates"
                  >
                    <Bell className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="flex-1 rounded-lg overflow-hidden min-h-0">
                {leftPanelView === 'youtube' && config?.youtube_url ? (
                  <iframe
                    width="100%"
                    height="100%"
                    src={getYouTubeEmbedUrl(config.youtube_url)}
                    title="YouTube Stream"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="rounded"
                  ></iframe>
                ) : leftPanelView === 'updates' ? (
                  <div className="h-full overflow-y-auto space-y-3 pr-2 min-w-0">
                    {recentUpdates.length > 0 ? (
                      recentUpdates.map((update, index) => {
                        const isNew = newUpdateIds.has(update.id);
                        return (
                        <div 
                          key={index} 
                          className={`rounded-lg p-4 border min-w-0 transition-all duration-500 ${
                            isNew 
                              ? 'bg-red-600/70 border-red-400 shadow-lg shadow-red-500/50' 
                              : 'bg-gray-700/50 border-gray-600'
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-3 min-w-0">
                            {update.author_id ? (
                              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                                <Avatar 
                                  userId={update.author_id} 
                                  userName={update.author}
                                  avatarUrl={userAvatars[update.author_id]}
                                  size="md" 
                                  className="w-full h-full" 
                                />
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center text-gray-300 text-sm font-medium flex-shrink-0">
                                {update.author ? update.author.charAt(0).toUpperCase() : '?'}
                              </div>
                            )}
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <div className="font-semibold text-base text-blue-400 truncate">
                                {update.project_ge} - {update.project_name}
                              </div>
                              <div className="text-xs text-gray-400 whitespace-nowrap">
                                {update.date} {update.time}
                              </div>
                            </div>
                          </div>
                          <div className="text-sm text-gray-300 leading-relaxed break-words">
                            {update.text}
                          </div>
                        </div>
                        );
                      })
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">
                        <p>No recent updates available</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <p>No content available</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Map / YouTube */}
            <div className="flex-1 bg-gray-800 rounded-lg p-3 min-w-0 flex flex-col" style={{flexBasis: '50%'}}>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  {rightPanelView === 'map' ? (
                    <>
                      <MapIcon className="w-5 h-5" />
                      Project Locations
                    </>
                  ) : (
                    <>
                      <Youtube className="w-5 h-5" />
                      Live Stream
                    </>
                  )}
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setRightPanelView('map')}
                    className={`p-2 rounded transition-colors ${
                      rightPanelView === 'map' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    title="Project Map"
                  >
                    <MapIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setRightPanelView('youtube')}
                    className={`p-2 rounded transition-colors ${
                      rightPanelView === 'youtube' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    title="YouTube Stream"
                  >
                    <Youtube className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex-1 rounded-lg overflow-hidden min-h-0">
                {rightPanelView === 'map' ? (
                  <MapContainer
                    center={[20, 0]}
                    zoom={config?.map_default_zoom || 2}
                    style={{ height: '100%', width: '100%' }}
                    className="z-0"
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MarkerClusterGroup
                      chunkedLoading
                      maxClusterRadius={50}
                    >
                      {liveData?.projects?.filter(p => {
                        if (!p.field_117_raw) return false;
                        
                        // Check if project status is enabled in map settings
                        const mapSettings = config?.map_settings || {};
                        const projectStatus = p.field_85_raw?.[0] || p.field_85_raw;
                        const statusId = projectStatus?.id;
                        
                        // If no map settings configured, show all
                        if (Object.keys(mapSettings).length === 0) return true;
                        
                        // Check if this status is enabled
                        return statusId && mapSettings[statusId]?.enabled;
                      }).map((project, idx) => {
                        const coords = getCountryCoordinates(project.field_117_raw);
                        if (coords[0] === 0 && coords[1] === 0) return null;
                        
                        // Get project status and use memoized icon
                        const projectStatus = project.field_85_raw?.[0] || project.field_85_raw;
                        const statusId = projectStatus?.id;
                        const customIcon = markerIcons[statusId] || markerIcons.default;
                        
                        return (
                          <Marker key={project.id || idx} position={coords} icon={customIcon}>
                            <Popup autoPan={false} closeButton={true}>
                              <div className="text-gray-900">
                                <strong>{project.field_5_raw || project.field_2_raw || 'Untitled'}</strong>
                                <br />
                                <span className="text-sm">{project.field_2_raw || project.field_117_raw}</span>
                                <br />
                                <span className="text-xs text-gray-600">Status: {projectStatus?.field_84_raw || 'Unknown'}</span>
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                    </MarkerClusterGroup>
                  </MapContainer>
                ) : rightPanelView === 'youtube' && config?.youtube_url ? (
                  <iframe
                    width="100%"
                    height="100%"
                    src={getYouTubeEmbedUrl(config.youtube_url)}
                    title="YouTube Stream"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="rounded"
                  ></iframe>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <p>No content available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right - Incident Stats & Exchange Rates */}
            <div className="flex flex-col gap-3 flex-shrink-0" style={{width: '320px'}}>
              {/* Incident Stats */}
              <div className="bg-gray-800 rounded-lg p-3">
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Incident Stats
                </h2>
                <div className="bg-red-900/30 rounded-lg p-3 border border-red-700">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-red-200">Unsolved Cases</span>
                      <span className="text-xl font-bold text-red-400">{liveData?.advanced_stats?.unsolved_cases || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-red-200">Average Age</span>
                      <span className="text-xl font-bold text-red-400">{liveData?.advanced_stats?.average_age_days || 0}d</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-red-200">Unsolved Actions</span>
                      <span className="text-xl font-bold text-red-400">{liveData?.advanced_stats?.unsolved_actions || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-red-200">Solutions to Review</span>
                      <span className="text-xl font-bold text-red-400">{liveData?.advanced_stats?.solutions_to_review || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exchange Rates */}
              <div className="bg-gray-800 rounded-lg p-3 flex-1">
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Exchange ({exchangeRates?.base || 'EUR'})
                </h2>
                <div className="bg-green-900/30 rounded-lg p-3 border border-green-700">
                  <div className="space-y-1.5">
                    {Object.entries(exchangeRates?.rates || {}).map(([currency, rate]) => (
                      <div key={currency} className="flex items-center justify-between py-1 border-b border-green-700/30 last:border-0">
                        <div className="flex items-center gap-2">
                          <FlagIcon countryCode={currencyCountries[currency] || 'UN'} />
                          <span className="font-mono font-bold">{currency}</span>
                        </div>
                        <span className="text-lg font-mono text-green-400">{rate?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row - Employees */}
          <div className="bg-gray-800 rounded-lg p-4 overflow-x-auto" style={{height: '35%'}}>
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Airport Division Team
            </h2>
            <div className="flex gap-4">
              {liveData?.employee_stats?.map((employee, idx) => {
                const avatarUrl = userAvatars[employee.id];
                return (
                <div key={employee.id} className="bg-gray-700 rounded-lg p-4 flex-shrink-0 w-64">
                  <div className="flex flex-col items-center mb-3">
                    <Avatar
                      userId={employee.id}
                      userName={employee.name}
                      avatarUrl={avatarUrl}
                      size="lg"
                    />
                    <div className="mt-2 text-center">
                      <div className="font-bold text-base truncate w-full">{employee.name || employee.email || 'Unknown'}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {config?.employee_stats?.includes('projects_assigned') && (
                      <div className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4 text-blue-400" />
                        <span>{employee.projects_assigned} Projects</span>
                      </div>
                    )}
                    {config?.employee_stats?.includes('tasks_completed') && (
                      <div className="flex items-center gap-1">
                        <CheckSquare className="w-4 h-4 text-green-400" />
                        <span>{employee.tasks_completed} Tasks</span>
                      </div>
                    )}
                    {config?.employee_stats?.includes('open_incidents') && (
                      <div className="flex items-center gap-1">
                        <AlertCircle className="w-4 h-4 text-purple-400" />
                        <span>{employee.open_incidents} Incidents</span>
                      </div>
                    )}
                    {config?.employee_stats?.includes('hours_this_week') && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-yellow-400" />
                        <span>{employee.hours_this_week}h Week</span>
                      </div>
                    )}
                    {employee.projects_no_recent_updates > 0 && (
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4 text-orange-400" />
                        <span>{employee.projects_no_recent_updates} Expired</span>
                      </div>
                    )}
                    {employee.incident_solutions_pending > 0 && (
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4 text-red-400" />
                        <span>{employee.incident_solutions_pending} solution</span>
                      </div>
                    )}
                  </div>
                </div>
              );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* News Ticker at Bottom */}
      <div className="h-12 flex-shrink-0 overflow-hidden relative" style={{ backgroundColor: config?.ticker_bg_color || '#B91C1C' }}>
        <div className="absolute inset-0 flex items-center">
          <div className="px-4 py-2 font-bold text-sm uppercase z-10 flex-shrink-0" style={{ 
            backgroundColor: config?.ticker_bg_color ? `${config.ticker_bg_color}DD` : '#991B1B',
            color: config?.ticker_text_color || '#FFFFFF'
          }}>
            Aviation News
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="animate-ticker whitespace-nowrap inline-block font-bold" style={{ color: config?.ticker_text_color || '#FFFFFF' }}>
              {news.map((item, idx) => (
                <span key={idx} className="inline-block mx-8">
                  <span className="font-extrabold">●</span> {item.title}
                </span>
              ))}
              {/* Duplicate for seamless loop */}
              {news.map((item, idx) => (
                <span key={`dup-${idx}`} className="inline-block mx-8">
                  <span className="font-extrabold">●</span> {item.title}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add CSS for ticker animation */}
      <style>{`
        @keyframes ticker {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-ticker {
          animation: ticker 60s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default CentralDashboard;
